import React, { useEffect, useState } from "react";
import { FileText, Trash2, Loader2, Upload } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { onWorkspaceChange } from "@/lib/active-workspace";

export default function Resumes() {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/resume/list");
      setResumes(data);
    } catch { /* transient, swallow */ } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const off = onWorkspaceChange(() => { load(); });
    return () => { off(); };
  }, []);

  const deleteResume = async (id) => {
    if (!window.confirm("Delete this resume?")) return;
    await api.delete(`/resume/${id}`);
    const { data } = await api.get("/resume/list");
    setResumes(data);
  };

  return (
    <div data-testid="resumes-page" className="animate-in max-w-3xl">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-2">Resumes</div>
        <h1 className="text-3xl font-bold tracking-tight text-zinc-50">Your resumes</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Every resume you&apos;ve uploaded or aligned. Manage or delete them here.
        </p>
      </div>

      {loading && (
        <div className="text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {!loading && resumes.length === 0 && (
        <div className="p-8 rounded-xl border border-zinc-800/70 bg-zinc-900/40 text-center">
          <FileText className="h-8 w-8 text-zinc-600 mx-auto mb-3" />
          <div className="text-sm text-zinc-300">No resumes yet.</div>
          <div className="text-xs text-zinc-500 mt-1">
            Upload one from Stand Out to get started.
          </div>
          <Link
            to="/app/resume"
            data-testid="resumes-empty-cta"
            className="mt-5 inline-flex items-center gap-2 text-sm bg-zinc-50 text-zinc-950 hover:bg-white font-semibold rounded-lg px-4 py-2"
          >
            <Upload className="h-3.5 w-3.5" /> Upload a resume
          </Link>
        </div>
      )}

      {!loading && resumes.length > 0 && (
        <div className="space-y-2">
          {resumes.map((r) => (
            <div
              key={r.resume_id}
              data-testid={`resume-item-${r.resume_id}`}
              className="p-4 rounded-lg border border-zinc-800 bg-zinc-900/40 flex items-center justify-between hover:border-zinc-700 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="h-4 w-4 text-zinc-500" />
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100 truncate">{r.name}</div>
                  <div className="text-xs text-zinc-500">{new Date(r.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              <button
                onClick={() => deleteResume(r.resume_id)}
                className="p-2 text-zinc-500 hover:text-rose-400 rounded-md hover:bg-zinc-800 transition-colors"
                data-testid={`resume-delete-${r.resume_id}`}
                title="Delete resume"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
